// Mirrors backend/tailor/service/node_agent_service.py: the first pytailor whose node
// agent understands `software_ref`. An older agent ignores the key and starts a default
// pytailor worker, so the backend refuses the command; the dialog disables the option.
export const MIN_SOFTWARE_REF_PYTAILOR_VERSION = '0.30.0';

// The shape backend/tailor/service/node_agent_service.py and pytailor's node agent apply,
// as a pre-check so a typo fails in the form instead of as a 400. Deliberately a strict
// subset: JS `\d` is ASCII-only while Python's matches any Unicode decimal digit, so a ref
// like `pkg@١.0` is refused here and accepted there. No Depot release carries such a
// version (they come from PEP 440 package versions), so the only refs this turns away are
// ones the agent would fail to resolve anyway — and loosening it would move that failure
// from the form to a START_FAILED worker. The backend stays the authority.
const SOFTWARE_REF_RE = /^[a-z0-9][a-z0-9._-]{0,63}(?:@\d+\.[A-Za-z0-9._-]{1,64}|:\d+-(?:next|stable))$/;

export function isSoftwareRef(value: string): boolean {
  return SOFTWARE_REF_RE.test(value);
}

function leadingVersion(version: string | null | undefined): [number, number, number] | null {
  const match = /^(\d+)\.(\d+)\.(\d+)/.exec(version ?? '');
  return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null;
}

export function agentVersionAtLeast(pytailorVersion: string | null | undefined, minimum: string): boolean {
  const actual = leadingVersion(pytailorVersion);
  const wanted = leadingVersion(minimum);
  if (!actual || !wanted) return false;
  for (let i = 0; i < 3; i += 1) {
    if (actual[i] !== wanted[i]) return actual[i] > wanted[i];
  }
  return true;
}

export function agentSupportsSoftwareRef(pytailorVersion: string | null | undefined): boolean {
  return agentVersionAtLeast(pytailorVersion, MIN_SOFTWARE_REF_PYTAILOR_VERSION);
}

const ALIAS_PART_RE = /^\d+-(?:next|stable)$/;

// `name@version` / `name:major-alias` → the name and the part after the separator.
export function splitSoftwareRef(ref: string): { name: string; part: string } {
  const match = /^([^@:]*)[@:](.*)$/.exec(ref);
  return match ? { name: match[1], part: match[2] } : { name: ref, part: '' };
}

// The upgrade dialog edits only the part after the name; an alias-shaped part
// (`0-stable`) rebuilds the alias form, anything else the pin form.
export function buildSoftwareRef(name: string, part: string): string {
  return ALIAS_PART_RE.test(part) ? `${name}:${part}` : `${name}@${part}`;
}
